import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Card, CardContent, Link, Skeleton, Typography } from '@mui/material'

import { DashboardSummary } from '../../types/Dashboard'

export enum DashboardCounter {
  WithoutAnalysis = 'withoutAnalysis',
  Outdated = 'outdated',
  NotUpdated = 'notUpdated',
}

type DashboardCountersProps = {
  summary?: DashboardSummary
  isLoading: boolean
  isError: boolean
  onOpen: (counter: DashboardCounter) => void
}

const CounterRow: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={4} py={0.5}>
    <Typography>{label}</Typography>
    <Typography fontWeight="bold" component="div">
      {children}
    </Typography>
  </Box>
)

export const DashboardCounters: FC<DashboardCountersProps> = ({ summary, isLoading, isError, onOpen }) => {
  const { t } = useTranslation()

  const count = (counter: DashboardCounter.WithoutAnalysis | DashboardCounter.Outdated, labelKey: string) => {
    if (isLoading) return <Skeleton variant="text" width={32} />
    if (isError || !summary) return '---'
    const value = summary[counter]
    if (!value) return 0
    return (
      <Link component="button" variant="body1" fontWeight="bold" aria-label={t(labelKey, { count: value })} onClick={() => onOpen(counter)}>
        {value}
      </Link>
    )
  }

  return (
    <Card sx={{ maxWidth: 560 }}>
      <CardContent>
        <CounterRow label={t('internal-analysts:dashboard_without_analysis')}>
          {count(DashboardCounter.WithoutAnalysis, 'internal-analysts:dashboard_without_analysis_open')}
        </CounterRow>
        <CounterRow label={t('internal-analysts:dashboard_outdated')}>
          {count(DashboardCounter.Outdated, 'internal-analysts:dashboard_outdated_open')}
        </CounterRow>
        <CounterRow label={t('internal-analysts:dashboard_not_updated')}>
          <Link component="button" variant="body1" fontWeight="bold" onClick={() => onOpen(DashboardCounter.NotUpdated)}>
            {t('internal-analysts:dashboard_show')}
          </Link>
        </CounterRow>
        {isError && (
          <Typography color="error.main" mt={1}>
            {t('internal-analysts:dashboard_summary_failed')}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}
